import React, { PureComponent, Suspense } from 'react';
import { array, bool, func, object, shape, string } from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { ShopMore } from '../../index';
import { ImageCarousel } from '@ynap/product-details-page';
import { OptimizelyFeature } from '@ynap/optimizely-utils';
import ViewportObserver from '@ynap/viewport-observer';
import processEditorialContent from '../../EditorialAccordion/processEditorialContent';
import { ProductDetailsSkeletonBasicInfo, ProductDetailsSkeletonDetails } from '@ynap/product-details-page/src/ProductDetailsSkeleton/ProductDetailsSkeleton';
import track, { TrackingPropType } from 'react-tracking';
import ProductDetails from './ProductDetails';
import { getFitAndMeasurements } from '../../EditorialAccordion/getEditorials';

import {
    trackSizeGuideOpen,
    trackSizeGuideClose,
    trackSizeGuide,
    trackImage,
    upsellNavigation,
    trackAddToBag,
    trackAddToWishlist,
    trackAddToBagError,
    trackAddToWishlistError,
    trackViewMoreNavigation,
    trackGoToBag,
    trackSizeAndFitSizeSelect,
} from '../../analytics';
import { withAnalytics, safeBuildEventObj } from '@ynap/analytics';
import DesignerDetails from '../../DesignerDetails/DesignerDetails';

const PDPOverlay = React.lazy(() => import(/* webpackChunkName: "PDPOverlay" */ './PDPOverlay'));
const Recommendations = React.lazy(
    () => import(/* webpackChunkName: "Recommendations" */ '@ynap/product-details-page/src/Recommendations/RecommendationsWrapper'),
);

const bem = new BEMHelper('ProductDetailsPage');

const heroImageSrcsetSize = '(min-width: 1024px) 560px, (min-width: 768px) 50vw, 100vw';
const fullBleedLayoutSize = '(min-width: 1920px) 75vw, (min-width: 1024px) 700px, (min-width: 768px) 50vw, 100vw';

@track()
class DefaultProductDetailsPageLayout extends PureComponent {
    constructor(props) {
        super(props);
        this.imageCarouselRef = React.createRef();

        this.state = {
            carouselTracked: false,
            modal: {
                key: null,
            },
            isEip: false,
            isMobileOrTablet: false,
        };

        this.trackedAddToWishlist = this.trackedAddToWishlist.bind(this);
        this.trackedAddToWishlistError = this.trackedAddToWishlistError.bind(this);
        this.trackedAddToBag = this.trackedAddToBag.bind(this);
        this.trackedAddToBagError = this.trackedAddToBagError.bind(this);
        this.trackedGoToBag = this.trackedGoToBag.bind(this);
        this.safeTrackEvent = this.safeTrackEvent.bind(this);
        this.setModal = this.setModal.bind(this);
        this.renderShopMore = this.renderShopMore.bind(this);
    }

    onModelClick = () => {
        const { config } = this.props;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        this.imageCarouselRef.current?.slideTo(1);
        const isfullBleedLayout = config.head.pdp?.fullBleedLayout?.enabled;

        (isfullBleedLayout || isMobile) && window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    setModal(key = null) {
        const closingModalKey = this.state.modal.key;
        if (closingModalKey === 'sizeChart') this.safeTrackEvent(trackSizeGuideClose);
        this.setState({
            modal: {
                key,
            },
        });
    }

    componentDidMount() {
        window?.SF?.getEipStatus?.().then(({ isEIP: isEip = false } = {}) => this.setState({ isEip }));

        this.media = window?.matchMedia?.('(min-width: 0px) and (max-width: 1023px)');
        this.setState({ isMobileOrTablet: this.media.matches || false });
        this.resizeHandler = () => {
            this.setState({ isMobileOrTablet: this.media.matches });
        };
        this.media.addEventListener('change', this.resizeHandler);
    }

    componentWillUnmount() {
        this.media.removeEventListener('change', this.resizeHandler);
    }

    static getDerivedStateFromProps(props, state) {
        if (props.loading) {
            return {
                carouselTracked: false,
            };
        }
        return state;
    }

    safeTrackEvent(buildAnalyticsFunc, ...args) {
        const { analytics, tracking } = this.props;
        tracking.trackEvent(safeBuildEventObj(buildAnalyticsFunc, analytics, ...args));
    }

    trackedAddToWishlist(sku, wishlistId) {
        const {
            addToWishlist,
            selectedSizeLabel,
            product: { size },
        } = this.props;
        return addToWishlist(sku, wishlistId)
            .then(() => {
                this.props?.optimizely?.track?.('plp_pdp_atw');
                this.props?.optimizely?.track?.('pdp_addtowishlist');
                return this.safeTrackEvent(trackAddToWishlist, sku, selectedSizeLabel, size.schemas);
            })
            .catch((error) => {
                return this.safeTrackEvent(trackAddToWishlistError, error.type);
                //add these to raygun (ISPW-415)
            }); //Catch any errors thrown from state
    }

    trackedAddToWishlistError() {
        this.safeTrackEvent(trackAddToWishlistError, 'NO_SIZE_SELECTED');
    }

    trackedAddToBag(sku, { setActionNotificationOnError, addToCartLoading, reservationId, includeShoppingCart }) {
        const {
            addToCart,
            product: { sizeLabel, size },
        } = this.props;

        return addToCart(sku, { setActionNotificationOnError, addToCartLoading, reservationId, includeShoppingCart })
            .then((response) => {
                if (response && response.type === 'ADD_TO_CART_SUCCESS') {
                    return this.safeTrackEvent(trackAddToBag, sku, sizeLabel, null, size.schemas);
                } else {
                    return this.safeTrackEvent(trackAddToBagError, response.type);
                }
            })
            .catch((e) => e); //Catch any errors thrown from state
    }

    trackedAddToBagError() {
        this.safeTrackEvent(trackAddToBagError, 'NO_SIZE_SELECTED');
    }

    trackedGoToBag(locale) {
        const { redirectToCheckout } = this.props;
        this.safeTrackEvent(trackGoToBag);
        return redirectToCheckout(locale);
    }

    measurementItem(measurement, index, sizeDetails) {
        // Handles multiple values and returns their measurement value if present. Will
        // effect bags or accessiories
        return (
            <li key={index}>
                {measurement.label && `${measurement.label}: `}
                {measurement.values.length > 1
                    ? measurement.values.map((value, index) => {
                          const defaultDetails = index === 0 ? `${value.label}${value?.uom}` : ` / ${value.label}${value?.uom}`;
                          const formattedDetails = sizeDetails.sizeDetails?.({
                              slash: index === 0 ? undefined : 'slash',
                              label: value?.label,
                              uom: value?.uom,
                          });

                          return formattedDetails || defaultDetails;
                      })
                    : `${measurement.values[0].label}`}
            </li>
        );
    }

    setSizeChartModal = (e, optimizely) => {
        e.preventDefault();
        this.safeTrackEvent(trackSizeGuideOpen);
        this.setModal('sizeChart');
        /* istanbul ignore next */
        optimizely?.track?.('click_view_size_guide');
    };

    renderShopMore = (modifier) => {
        const { product, messages, locale, mappedCategories, generateListingUrl, plpUrlTemplate, resetPLPState } = this.props;

        return (
            <ShopMore
                className={bem('shopMore', modifier)}
                messages={messages}
                product={product}
                mappedCategories={mappedCategories}
                generateListingUrl={generateListingUrl}
                plpUrlTemplate={plpUrlTemplate}
                locale={locale}
                linkOnClick={() => {
                    resetPLPState();
                    this.safeTrackEvent(trackViewMoreNavigation);
                }}
                ignoreCategories={this.props?.config?.head?.plp?.facet?.ignoreCategories}
            />
        );
    };

    render() {
        const {
            product,
            messages,
            region,
            locale,
            urlConfig,
            internal_urls,
            language,
            bypassWCSRecommendations,
            features,
            ssr,
            sizeGridFeatureToggle,
            resetPLPState,
            tracking,
            allow1stLevelZoom,
            optimizely,
            loading,
            config,
            wasReducedPDP,
        } = this.props;

        const { head: { pdp: { vtoScriptUrl = '', vtoCssUrl = '', stickyCTAButton = false } = {} } = {} } = config;
        const fullBleedLayout = config.head.pdp?.fullBleedLayout?.enabled;
        const showThumbnails = !fullBleedLayout;
        const showVerticalLayout = fullBleedLayout && !this.state.isMobileOrTablet;

        if (loading) {
            return (
                <div className={bem('wrapper', { fullBleedLayout })}>
                    <div className={bem('productDetailsGrid', { stickyCta: stickyCTAButton, fullBleedLayout })}>
                        <ImageCarousel
                            thumbnailsSrcsetSize="48px"
                            className={{
                                thumbnailsClassName: bem('imageCarouselThumbnails'),
                                carouselClassName: bem('imageCarousel', 'fullBleedLayout'),
                                carouselGridClassName: bem('imageCarouselGrid', { vertical: showVerticalLayout }),
                                carouselGridStickyClassName: bem('imageCarouselGrid', 'sticky'),
                            }}
                            vertical={showVerticalLayout}
                            showThumbnails={showThumbnails}
                            loading={true}
                        />
                        <ProductDetailsSkeletonBasicInfo className={bem('productDetailsSkeleton')} />
                        <ProductDetailsSkeletonDetails className={bem('productDetailsSkeleton')} />
                    </div>
                </div>
            );
        }

        const {
            name,
            designerName,
            designerSeo,
            images,
            videos,
            videosFirst,
            skus,
            sizeLabel,
            productNotes,
            sizeMeasurement,
            sizeConversion,
            sizeConversionBySku,
            measurementType,
            frontImage,
            masterCategory,
            colourPartNumber,
            imageViews,
            sizeScheme,
            analyticsAttributes,
        } = product;

        const { sizeAndFit, fitAndMeasurements } = productNotes;
        const productMeasurementsEnabled = config?.head?.pdp?.productMeasurements;
        const measurements = getFitAndMeasurements(fitAndMeasurements, messages.sizeAndFit, productMeasurementsEnabled);
        const sizeAndFitInformation = (sizeAndFit || measurements) && (
            <>
                {processEditorialContent({
                    content: sizeAndFit,
                    urlConfig,
                    internal_urls,
                    locale,
                    selectedPartNumber: colourPartNumber,
                    editorialId: 'SIZE_CHART',
                })}
                {measurements}
            </>
        );

        const isDesignerDetailsVisible = !!(config.head.pdp.enableCoreMediaDesignerDetails && designerSeo);

        return (
            <div className={bem('wrapper', { fullBleedLayout })}>
                <div className={bem('productDetailsGrid', { stickyCta: stickyCTAButton, fullBleedLayout })}>
                    {this.state.modal.key && (
                        <Suspense fallback={''}>
                            <PDPOverlay
                                name={name}
                                frontImage={frontImage}
                                skus={skus}
                                sizeLabel={sizeLabel}
                                designerName={designerName}
                                content={this.state.modal.key}
                                {...this.props}
                                setModal={this.setModal}
                                sizeAndFitInformation={sizeAndFitInformation}
                                sizeMeasurement={sizeMeasurement}
                                sizeConversion={sizeConversion}
                                sizeConversionBySku={sizeConversionBySku}
                                masterCategory={masterCategory}
                                trackSizeAndFitSizeSelect={trackSizeAndFitSizeSelect}
                                safeTrackEvent={this.safeTrackEvent}
                                trackSizeGuide={trackSizeGuide}
                                measurementType={measurementType}
                                sizeScheme={sizeScheme}
                                isEip={this.state.isEip}
                            />
                        </Suspense>
                    )}

                    <OptimizelyFeature feature={['web00729_virtualtryon_pdp_responsive']}>
                        {(props) => {
                            const { isEnabled, variationKey } = props;
                            const isVTOEnabled = variationKey === 'v1_web00729_virtualtryon';

                            return (
                                <ImageCarousel
                                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                                    images={images}
                                    imageSrcsetSize={fullBleedLayout ? fullBleedLayoutSize : heroImageSrcsetSize}
                                    videos={videos}
                                    thumbnailsSrcsetSize="120px" // 120px for both retina and non-retina screens
                                    className={{
                                        thumbnailsClassName: bem('imageCarouselThumbnails'),
                                        carouselClassName: bem('imageCarousel', { fullBleedLayout }),
                                        carouselGridClassName: bem('imageCarouselGrid', { fullBleedLayout }),
                                        carouselGridStickyClassName: bem('imageCarouselGrid', { sticky: true, fullBleedLayout }),
                                    }}
                                    showThumbnails={showThumbnails}
                                    vertical={showVerticalLayout}
                                    allow1stLevelZoom={allow1stLevelZoom}
                                    allow2ndLevelZoom={true}
                                    onInteraction={() => {
                                        !this.state.carouselTracked && this.safeTrackEvent(trackImage, 'carousel') && this.setState({ carouselTracked: true });
                                    }}
                                    videosFirst={videosFirst}
                                    ssr={ssr}
                                    NAPImageCarouselModifier={'layoutDefaultView'}
                                    isEnabled={isEnabled}
                                    isVTOEnabled={isVTOEnabled}
                                    vtoScriptUrl={vtoScriptUrl}
                                    vtoCssUrl={vtoCssUrl}
                                    variation={variationKey}
                                    colourPartNumber={colourPartNumber}
                                    ref={this.imageCarouselRef}
                                >
                                    {this.renderShopMore({ carousel: true, fullBleedLayout })}
                                </ImageCarousel>
                            );
                        }}
                    </OptimizelyFeature>

                    <ProductDetails
                        {...this.props}
                        sizeAndFitInformation={sizeAndFitInformation}
                        sizeGridFeatureToggle={sizeGridFeatureToggle}
                        measurements={measurements}
                        setSizeChartModal={(e) => this.setSizeChartModal(e, optimizely)}
                        redirectToWishlist={this.props.redirectToWishlist}
                        tracking={{
                            trackedAddToBag: this.trackedAddToBag,
                            trackedAddToBagError: this.trackedAddToBagError,
                            trackedGoToBag: this.trackedGoToBag,
                            trackedAddToWishlist: this.trackedAddToWishlist,
                            trackedAddToWishlistError: this.trackedAddToWishlistError,
                            safeTrackEvent: this.safeTrackEvent,
                            trackEvent: tracking?.trackEvent,
                        }}
                        setModal={this.setModal}
                        designerLinkOnClick={() => resetPLPState()}
                        partNumber={product.colourPartNumber}
                        onModelSizeClick={this.onModelClick}
                        isEip={this.state.isEip}
                        stickyCta={stickyCTAButton}
                    />

                    {this.renderShopMore({ bottomDetails: true, fullBleedLayout })}
                </div>
                <div className={bem('recommendationsGrid')}>
                    <ViewportObserver className={bem('recommendationsWrapper', { fullBleedLayout })} innerKey={product.colourPartNumber}>
                        {(intersected) =>
                            intersected && (
                                <Suspense fallback={''}>
                                    <Recommendations
                                        imageViews={imageViews}
                                        messages={messages}
                                        region={region}
                                        colourPartNumber={product.colourPartNumber}
                                        modelPartNumber={product.modelPartNumber}
                                        recommendationsToFetch={product.recommendationsToFetch}
                                        isCarouselVisible={wasReducedPDP ? false : config.head.pdp.enableRecommendationsCarousel}
                                        ymalFirst={features?.ymalFirst || wasReducedPDP}
                                        imageTemplate={product.imageTemplate}
                                        tracking={{
                                            wiw: (product, position) => {
                                                this.safeTrackEvent(
                                                    upsellNavigation,
                                                    product,
                                                    position,
                                                    'upsell - wear it with',
                                                    `wear it with - look number ${position}`,
                                                );
                                                /* istanbul ignore next */
                                                optimizely?.track?.('click_upsell_modules');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('clicks_on_WIW');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('pdp_recommendations_click');
                                            },
                                            ymal: (product, position) => {
                                                this.safeTrackEvent(upsellNavigation, product, position, 'upsell - ymal', 'ymal - module');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('click_upsell_modules');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('clicks_on_YMAL');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('pdp_recommendations_click');
                                            },
                                            rv: (product, position) => {
                                                this.safeTrackEvent(upsellNavigation, product, position, 'upsell - rv', 'rv - module');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('click_upsell_modules');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('pdp_rv_click');
                                                /* istanbul ignore next */
                                                optimizely?.track?.('pdp_recommendations_click');
                                            },
                                            trackEvent: tracking.trackEvent,
                                        }}
                                        bypassWCSRecommendations={bypassWCSRecommendations}
                                        useOutfitView={config.head.pdp.useOutfitView}
                                        images={images[0]?.url}
                                        reducedMode={wasReducedPDP}
                                    />
                                </Suspense>
                            )
                        }
                    </ViewportObserver>
                    {isDesignerDetailsVisible && (
                        <ViewportObserver className={bem('designerDetails')} innerKey={designerSeo.seoURLKeyword}>
                            {(intersected) => intersected && <DesignerDetails designerSeo={designerSeo} gender={analyticsAttributes.gender}></DesignerDetails>}
                        </ViewportObserver>
                    )}
                </div>
            </div>
        );
    }
}

DefaultProductDetailsPageLayout.propTypes = {
    fetchWishlists: func,
    product: object.isRequired,
    selectedProductVariant: object.isRequired,
    selectedSku: object,
    selectedSizeLabel: string.isRequired,
    messages: shape({
        colourAndSize: shape({
            colour: func.isRequired,
        }).isRequired,
        sizes: shape({
            sizeLabel: func.isRequired,
            oneSizeLabel: func.isRequired,
            initialValue: func.isRequired,
            sizeConversion: func.isRequired,
            sizeGuide: shape({
                viewProductMeasurements: func.isRequired,
                viewSizeGuide: func.isRequired,
                title: func.isRequired,
                sizeMeasurementsTitle: func.isRequired,
                sizeMeasurementsDetails: func.isRequired,
                sizeConversionsTitle: func.isRequired,
                sizeConversionsDetails: func.isRequired,
                slideToScroll: func.isRequired,
                cm: func.isRequired,
                in: func.isRequired,
            }).isRequired,
        }).isRequired,
        detailsAndCare: shape({
            madeIn: func.isRequired,
            imported: func.isRequired,
        }).isRequired,
        accordionTitles: shape({
            editorsNotes: func.isRequired,
            imported: func.isRequired,
            sizeAndFit: func.isRequired,
            detailsAndCare: func.isRequired,
            technicalDescription: func.isRequired,
        }).isRequired,
        accordionIds: shape({
            editorsNotes: func.isRequired,
            imported: func.isRequired,
            sizeAndFit: func.isRequired,
            detailsAndCare: func.isRequired,
            technicalDescription: func.isRequired,
        }).isRequired,
        product: shape({
            needAdvice: func.isRequired,
            needAdviceCTA: func.isRequired,
        }).isRequired,
        deliveryAndReturns: shape({}).isRequired,
        error: shape({}).isRequired,
    }),
    changeSelectedSku: func.isRequired,
    addToCart: func.isRequired,
    addToCartLoading: bool,
    addToWishlist: func.isRequired,
    addToWishlistError: string,
    urlCustomerReturns: string,
    addToWishlistLoading: bool,
    wishlists: array,
    region: string.isRequired,
    locale: string.isRequired,
    partNumberTemplate: string.isRequired,
    plpUrlTemplate: string.isRequired,
    bypassWCSRecommendations: func,
    mappedCategories: shape({
        seo: shape({ seoURLKeyword: string.isRequired }),
        child: object.isRequired,
    }),
    generateListingUrl: func.isRequired,
    errors: shape({
        addToCart: string,
    }),
    loading: bool.isRequired,
    actionsErrors: object,
    actionsLoading: object,
    sizeGridFeatureToggle: bool,
    sizeLabelToggle: bool,
    userFetchedFromSF: bool,
    analytics: shape({
        category: shape({
            pageType: string.isRequired,
            subCategory1: string.isRequired,
        }),
    }),
    tracking: TrackingPropType,
    urlConfig: object,
    internal_urls: array,
    config: object,
    language: string.isRequired,
    features: object,
    ssr: bool,
    resetPLPState: func,
    productInDC3: bool,
    redirectToCheckout: func,
    redirectToWishlist: func,
    allow1stLevelZoom: bool,
    wasReducedPDP: bool,
};

export { DefaultProductDetailsPageLayout };
export default withAnalytics(DefaultProductDetailsPageLayout);
